import { layers } from "@smartsheet/design-tokens";
import { useForkRef, useResizeObserver } from "@smartsheet/lodestar-utils";
import { useState, useRef, useCallback, Ref, FocusEventHandler, MouseEventHandler } from "react";
import { useTooltip } from "../components/tooltip";
import { UseTooltipResult } from "../components/tooltip/useTooltip";

interface UseOverflowTooltipOptions<MeasureElementType extends HTMLElement, TargetElementType extends HTMLElement> {
    elementIsOverflowing: (element: MeasureElementType) => boolean;
    tooltipTargetRef: Ref<TargetElementType>;
    isDisabled?: boolean;
    onBlur?: FocusEventHandler<TargetElementType>;
    onFocus?: FocusEventHandler<TargetElementType>;
    onMouseEnter?: MouseEventHandler<TargetElementType>;
    onMouseLeave?: MouseEventHandler<TargetElementType>;
}

interface UseOverflowTooltipResult<MeasureElementType extends HTMLElement, TargetElementType extends HTMLElement> {
    elementToMeasureRef: Ref<MeasureElementType>;
    showOverflowTooltip: boolean;
    targetProps: Omit<UseTooltipResult<TargetElementType>["targetProps"], "aria-describedby">;
    tooltipProps: UseTooltipResult<TargetElementType>["tooltipProps"];
}

export const useOverflowTooltip = <MeasureElementType extends HTMLElement, TargetElementType extends HTMLElement>({
    isDisabled,
    elementIsOverflowing,
    onBlur,
    onFocus,
    onMouseEnter,
    onMouseLeave,
    tooltipTargetRef,
}: UseOverflowTooltipOptions<MeasureElementType, TargetElementType>): UseOverflowTooltipResult<
    MeasureElementType,
    TargetElementType
> => {
    const [showOverflowTooltip, setShowOverflowTooltip] = useState(false);
    const elementToMeasureRefLocal = useRef<MeasureElementType>(null);

    const onResize = useCallback(() => {
        if (elementToMeasureRefLocal.current) {
            setShowOverflowTooltip(elementIsOverflowing(elementToMeasureRefLocal.current));
        }
    }, [elementIsOverflowing]);

    const { ref: resizeObserverRef } = useResizeObserver(onResize, {
        debounceMs: 1000,
        enabled: !isDisabled,
    });

    const { tooltipProps, targetProps } = useTooltip<TargetElementType>({
        targetRef: tooltipTargetRef,
        onBlur,
        onFocus,
        onMouseEnter,
        onMouseLeave,
        portalProps: {
            zIndex: layers.tooltip,
        },
    });

    // Since the text is only elided, screen readers can still see it in the DOM.
    // We'll remove this linkage so a SR won't double-read the text AND the tooltip.
    delete targetProps["aria-describedby"];

    const elementToMeasureRef = useForkRef(resizeObserverRef, elementToMeasureRefLocal);

    return {
        showOverflowTooltip,
        tooltipProps,
        targetProps,
        elementToMeasureRef,
    };
};
